/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { createRef, useEffect, useRef, useState } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import Button from '@stories/atoms/Button';
import Checkbox from '@stories/atoms/FormElements/Checkbox';
import { SectionShort } from '@stories/atoms/Section/Section';
import { media } from '@stories/theming/settings';

import Happy from './icons/happy.svg';
import Neutral from './icons/neutral.svg';
import Unhappy from './icons/unhappy.svg';
import { translations } from './translations';

const moods = {
  happy: 'happy',
  unhappy: 'unhappy',
  neutral: 'neutral'
};

const steps = {
  form: 'form',
  error: 'error',
  success: 'success'
};
const feedbackformStyles = {
  main: css({
    input: { display: 'none' },
    label: { padding: '0.5rem' },
    padding: '1rem'
  }),
  comment: (t) =>
    css({
      fontSize: t.font.size.small,
      fontWeight: 'normal',
      maxWidth: 'none',
      width: '100%'
    }),
  moods: {
    happy: css({
      height: '3.5rem',
      width: '3.5rem',
      fill: 'hsl(0, 0%, 25%)',
      '&:hover': { fill: 'hsl(92, 62%, 41%)' }
    }),
    unhappy: css({
      height: '3.5rem',
      width: '3.5rem',
      fill: 'hsl(0, 0%, 25%)',
      '&:hover': { fill: 'hsl(353, 98%, 41%)' }
    }),
    neutral: css({
      height: '3.5rem',
      width: '3.5rem',
      fill: 'hsl(0, 0%, 25%)',
      '&:hover': { fill: 'hsl(39, 78%, 64%)' }
    })
  },
  svg: {
    fill: 'hsl(0, 0%, 25%)',
    left: 0,
    top: 0,
    transition: 'fill 200ms ease-out'
  }
};
const formAction = '/feedback/index/post/';
const topics = {
  unhappy: [
    'Product availability',
    'Problem with coupon / offer',
    'Customer service',
    'Technical issue',
    'Page layout',
    'Search',
    'Product selection',
    'Shipping',
    'Product information',
    'Ordering process',
    'Other'
  ],
  neutral: [
    'Product availability',
    'Problem with coupon / offer',
    'Customer service',
    'Technical issue',
    'Page layout',
    'Search',
    'Product selection',
    'Shipping',
    'Product information',
    'Ordering process',
    'Other'
  ],
  happy: [
    'Product availability',
    'Product information',
    'Price/Performance',
    'Design',
    'Page layout',
    'Product quality',
    'Good promotions',
    'Simple ordering process',
    'Tips, tools & recipes',
    'Other'
  ]
};
const Success = ({ mood }) => {
  const translate = useTranslate(translations);

  return (
    <div>
      <h2 css={(t) => ({ ...t.headline.h3 })}>{translate('Your feedback is on the way!')}</h2>
      <div css={() => ({ marginTop: '2rem' })}>
        <span>
          {mood === mood.happy
            ? translate('Thank you for taking your time. We are constantly working to improve our site.')
            : translate(
                'Thank you for taking your time. We will be entitled to our site as soon as possible.'
              )}
        </span>
        <span>{translate('We hope you enjoy shopping!')}</span>
      </div>
    </div>
  );
};

const Error = () => {
  const translate = useTranslate(translations);

  return (
    <div>
      <h2 css={(t) => ({ ...t.headline.h3 })}>{translate('Sorry, something went wrong.')}</h2>
      <div>
        <span>
          {translate(
            'There seems to be a technical problem blocking the sending of your feedback. We have already been automatically informed of this error and will correct it as soon as possible.'
          )}
        </span>
      </div>
    </div>
  );
};

// TODO: Fix any, do not create types like this
interface IFeedbackForm {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  localeProp?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  moodProp?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stepProp?: any;
}

const FeedbackForm: React.FC<IFeedbackForm> = ({ moodProp, stepProp }) => {
  const locale = 'de_DE';
  const __ = ({ val }) => translations[val]?.[locale];

  const lastCheckboxRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [mood, setMood] = useState(moodProp || null);
  const [selectedTopics, setSelectedTopics] = useState(0);
  const [step, setStep] = useState(stepProp || steps.form);
  // const [captchaToken, setCaptchaToken] = useState('');
  // const recaptchaRef = createRef<any>();
  const sectionRef = createRef<HTMLDivElement>();
  const countSelectedTopics = (e) => {
    const listCheckboxesChecked = document.querySelectorAll(
      `[name=${e.target.getAttribute('name')}]:checked`
    );
    setSelectedTopics(listCheckboxesChecked.length);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(formRef.current ?? undefined);
    formData.append('currenturl', window.location.href);
    formData.append('useragent', navigator.userAgent);
    // recaptchaRef.current && recaptchaRef.current.execute();
    fetch(formAction, {
      method: 'POST',
      body: formData,
      headers: new Headers({ 'X-Requested-With': 'XMLHttpRequest' })
    })
      .then(() => {
        setStep(steps.success);
      })
      .catch(() => {
        setStep(steps.error);
      });
  };

  useEffect(() => {
    if (lastCheckboxRef.current) {
      lastCheckboxRef?.current?.setCustomValidity(
        selectedTopics > 0 ? '' : __({ val: 'Please choose at least one Topic' })
      );
    }
  }, [selectedTopics, mood, step]);

  useEffect(() => {
    if (step !== steps.form) {
      sectionRef.current && sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [sectionRef, step]);

  const formMarkup = (
    <form
      action={formAction}
      method="POST"
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      onSubmit={onSubmit}
      ref={formRef}
    >
      <input type="hidden" name="type" value="vote" />
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <h2
          css={(t) =>
            css({
              fontFamily: t.font.family.headline,
              fontSize: '24px',
              fontWeight: 'bold'
            })
          }
        >
          {__({
            val: 'How satisfied are you with our site?'
          })}
        </h2>
        <div css={feedbackformStyles.main}>
          <label onClick={() => setMood(moods.unhappy)}>
            <input type="radio" name="vote" value="unhappy" required />{' '}
            <Unhappy
              css={feedbackformStyles.moods.unhappy}
              style={mood === moods.unhappy ? { fill: 'hsl(353, 98%, 41%)' } : undefined}
            />
          </label>
          <label onClick={() => setMood(moods.neutral)}>
            <input type="radio" name="vote" value="neutral" required />{' '}
            <Neutral
              css={feedbackformStyles.moods.neutral}
              style={mood === moods.neutral ? { fill: 'hsl(39, 78%, 64%)' } : undefined}
            />
          </label>
          <label onClick={() => setMood(moods.happy)}>
            <input type="radio" name="vote" value="happy" required />{' '}
            <Happy
              css={feedbackformStyles.moods.happy}
              style={mood === moods.happy ? { fill: 'hsl(92, 62%, 41%)' } : undefined}
            />
          </label>
        </div>
      </div>

      <div style={{ display: mood ? 'block' : 'none' }}>
        <h3
          css={(t) =>
            css({
              fontFamily: t.font.family.headline,
              fontSize: '24px',
              fontWeight: 'bold'
            })
          }
        >
          {mood === 'happy'
            ? __({ val: 'We are glad to hear that!' })
            : __({ val: 'What can we do better?' })}
        </h3>
      </div>
      <div
        style={{
          display: mood ? 'flex' : 'none'
        }}
        css={{
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100%',
          padding: '1.25rem 0',
          [media('desktop')]: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'flex-start'
          }
        }}
      >
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            paddingBottom: '1.25rem',
            [media('desktop')]: {
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              flexDirection: 'row',
              width: '50%'
            }
          }}
        >
          {mood &&
            topics[mood].map((label, i) => (
              <Checkbox
                cssProps={{ padding: '0.5rem' }}
                name="topic"
                value={label}
                key={mood + label}
                onChange={countSelectedTopics}
                inputRef={i === topics[mood].length - 1 ? lastCheckboxRef : null}
              >
                {__({ val: label })}
              </Checkbox>
            ))}
        </div>
        <div
          css={{
            paddingBottom: '1.25rem',
            width: '100%',
            [media('desktop')]: {
              width: '50%',
              padding: '1rem 0 0 1.25rem'
            }
          }}
        >
          <label css={feedbackformStyles.comment}>
            <textarea
              name="comment"
              css={(t) =>
                css({
                  padding: '1.25rem',
                  fontFamily: t.font.family.copy,
                  fontSize: t.font.size.copy,
                  minHeight: '10rem',
                  width: '100%',
                  [media('desktop')]: { minHeight: '6rem' },
                  '&:focus': { border: '1px solid hsl(92, 62%, 41%)' }
                })
              }
              placeholder={__({
                val: 'Tell us briefly what makes you happy or what we could improve.'
              })}
              required
            />
          </label>
        </div>
      </div>
      {/* <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={CAPTCHA_PUBLIC_KEY}
        onChange={captchaOnChange}
      /> */}
      <div style={{ display: mood ? 'flex' : 'none' }}>
        <Button buttonStyle="secondary" label={__({ val: 'Send feedback' })} attrs={{ type: 'submit' }} />
      </div>
    </form>
  );

  return (
    <div ref={sectionRef}>
      <SectionShort>
        {step === steps.form && formMarkup}
        {step === steps.success && <Success {...{ mood }} />}
        {step === steps.error && <Error />}
      </SectionShort>
    </div>
  );
};

export default FeedbackForm;
